import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import { type RefObject, useRef, useState } from 'react';
import { useEventListener } from 'usehooks-ts';
import { CHAT_WIDGET_SIDEBAR_MIN_WIDTH, clampChatWidgetSidebarWidth, getChatWidgetSidebarMaxWidth } from '../../hooks/use-chat-widget-sidebar-width';

// Pixel resize handle for the docked sidebar; width = container right edge - cursor.
type ChatWidgetSidebarSplitterProps = {
  containerRef: RefObject<HTMLElement | null>;
  width: number;
  onDrag: (width: number) => void;
  onDragEnd: (width: number) => void;
  onDragStart?: () => void;
};
const KEYBOARD_STEP = 24;
const ChatWidgetSidebarSplitter = t0 => {
  const $ = _c(20);
  const {
    containerRef,
    width,
    onDrag,
    onDragEnd,
    onDragStart
  } = t0;
  const [isDragging, setIsDragging] = useState(false);
  const latestWidthRef = useRef(null);
  let t1;
  if ($[0] !== containerRef || $[1] !== isDragging || $[2] !== onDrag) {
    t1 = e => {
      if (!isDragging) {
        return;
      }
      const el = containerRef.current;
      if (!el) {
        return;
      }
      const rightEdge = el.getBoundingClientRect().right;
      const next = clampChatWidgetSidebarWidth(rightEdge - e.clientX);
      latestWidthRef.current = next;
      onDrag(next);
    };
    $[0] = containerRef;
    $[1] = isDragging;
    $[2] = onDrag;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useEventListener("mousemove", t1);
  let t2;
  if ($[4] !== isDragging || $[5] !== onDragEnd) {
    t2 = () => {
      if (!isDragging) {
        return;
      }
      setIsDragging(false);
      if (latestWidthRef.current !== null) {
        onDragEnd(latestWidthRef.current);
      }
    };
    $[4] = isDragging;
    $[5] = onDragEnd;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  useEventListener("mouseup", t2);
  let t3;
  if ($[7] !== onDragEnd || $[8] !== width) {
    t3 = e_0 => {
      if (e_0.key === "ArrowLeft") {
        e_0.preventDefault();
        onDragEnd(clampChatWidgetSidebarWidth(width + KEYBOARD_STEP));
      } else {
        if (e_0.key === "ArrowRight") {
          e_0.preventDefault();
          onDragEnd(clampChatWidgetSidebarWidth(width - KEYBOARD_STEP));
        }
      }
    };
    $[7] = onDragEnd;
    $[8] = width;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleKeyDown = t3;
  let t4;
  if ($[10] !== width) {
    t4 = Math.round(width);
    $[10] = width;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = getChatWidgetSidebarMaxWidth();
    t6 = cx("relative w-2 shrink-0 cursor-col-resize select-none outline-none");
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  let t7;
  if ($[14] !== onDragStart) {
    t7 = e_1 => {
      e_1.preventDefault();
      setIsDragging(true);
      onDragStart?.();
    };
    $[14] = onDragStart;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== handleKeyDown || $[17] !== t4 || $[18] !== t7) {
    t8 = <div role="separator" tabIndex={0} aria-orientation="vertical" aria-label="Resize chat sidebar" aria-valuenow={t4} aria-valuemin={CHAT_WIDGET_SIDEBAR_MIN_WIDTH} aria-valuemax={t5} className={t6} onMouseDown={t7} onKeyDown={handleKeyDown} />;
    $[16] = handleKeyDown;
    $[17] = t4;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
};
export default ChatWidgetSidebarSplitter;